import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { components } from '../../components';
import { hooks } from '../../hooks';
import { items } from '../../items';
import { Routes } from '../../routes';
import { RootState } from '../../store';
import type { DishType } from '../../types';
import { getItemFromLocalStorage } from '../../utils/getItemFromLocalStorage';

export const Order: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const [opacity, setOpacity] = useState<number>(0);
  const MENU_CONFIG = getItemFromLocalStorage("menuConfiguration");
  const [showQR, setShowQR] = useState(false);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const navigate = hooks.useNavigate();

  const { menuLoading, menu } = hooks.useGetMenu();

  const { list, subtotal, delivery, total } = useSelector(
    (state: RootState) => state.cartSlice,
  );

  const [fingerprint, setFingerprint] = useState<string>('');

  useEffect(() => {
    FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => {
        const visitorId = result.visitorId;
        setFingerprint(visitorId);
      });
  }, []);

  // Prepare QR payload: sending user fingerprint, table, and order items
  const qrData = JSON.stringify({
    user: fingerprint,
    selectedTable: localStorage.getItem("TABLE_ID") || "",
    items: list.map((dish) => ({
      id: dish._id,
      name: dish.name,
      qty: dish.quantity,
      isFullPrice: dish.isFullPrice !== false, // Include full/half information
    })),
  });

  const renderDishes = (): JSX.Element => {
    return (
      <section style={{ marginBottom: 24, padding: '0 20px' }}>
        <ul style={{ paddingTop: 10, listStyle: 'none', margin: 0 }}>
          {list.map((dish: DishType, index: number, array: DishType[]) => {
            const isLast = index === array.length - 1;
            return (
              <items.OrderItem key={dish._id} isLast={isLast} dish={dish} />
            );
          })}
        </ul>
      </section>
    );
  };

  const renderSummary = (): JSX.Element => {
    return (
      <section
        style={{
          padding: 24,
          borderRadius: 20,
          margin: '0 20px 24px',
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 176, 185, 0.15)',
          boxShadow: '0 4px 20px rgba(0, 176, 185, 0.08)',
        }}
      >
        <h3 style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--main-color)',
          marginBottom: 20,
          marginTop: 0,
        }}>
          Order Summary
        </h3>
        <div className="row-center-space-between" style={{ marginBottom: 16 }}>
          <span style={{ 
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            color: '#666',
            fontWeight: 500 
          }}>
            Subtotal
          </span>
          <span style={{ 
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            color: 'var(--main-color)',
            fontWeight: 600 
          }}>
            ${subtotal.toFixed(2)}
          </span>
        </div>
        <div
          className="row-center-space-between"
          style={{
            paddingBottom: 16,
            marginBottom: 20,
            borderBottom: '1px solid rgba(0, 176, 185, 0.1)',
          }}
        >
          <span style={{ 
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            color: '#666',
            fontWeight: 500 
          }}>
            Delivery
          </span>
          <span style={{ 
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            color: 'var(--main-color)',
            fontWeight: 600 
          }}>
            ${delivery}
          </span>
        </div>
        <div className="row-center-space-between">
          <h4 style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--main-color)',
            margin: 0,
          }}>
            Total
          </h4>
          <h4 style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--main-turquoise)',
            margin: 0,
          }}>
            ${total.toFixed(2)}
          </h4>
        </div>
      </section>
    );
  };

  const renderButton = (): JSX.Element => {
    return (
      <div style={{ padding: '0 20px', marginBottom: 100 }}>
        <components.Button
          text="Proceed to Checkout"
          onClick={() => {
            navigate(Routes.Checkout);
          }}
        />
      </div>
    );
  };

  const renderContent = (): JSX.Element | null => {
    if (list.length === 0 && !menuLoading) return null;
    return (
      <main className="scrollable" style={{ paddingBottom: 120 }}>
        {renderDishes()}
        {MENU_CONFIG?.isOrderFromAppSupported && renderSummary()}
        {MENU_CONFIG?.isOrderFromAppSupported && renderButton()}
      </main>
    );
  };

  const renderEmpty = (): JSX.Element | null => {
    if (list.length === 0 && !menuLoading) {
      return (
        <main className="scrollable container">
          <section
            style={{
              paddingTop: '8%',
              paddingBottom: '14%',
              paddingLeft: 20,
              paddingRight: 20,
              borderRadius: 20,
              marginTop: 20,
              backgroundColor: '#ffffff',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
            }}
          >
            <img
              src="https://george-fx.github.io/dinehub_api/assets/images/02.jpg"
              alt="Empty cart"
              style={{
                width: 'calc(100% - 80px)',
                maxWidth: 280,
                height: 'auto',
                marginBottom: 24,
                marginLeft: 'auto',
                marginRight: 'auto',
                display: 'block',
              }}
            />
            <h2
              style={{
                fontFamily: 'DM Sans, sans-serif',
                textAlign: 'center',
                textTransform: 'capitalize',
                marginBottom: 12,
                fontSize: 24,
                fontWeight: 700,
                color: 'var(--main-color)',
              }}
            >
              Your cart is empty!
            </h2>
            <p style={{ 
              fontFamily: 'DM Sans, sans-serif',
              textAlign: 'center',
              fontSize: 16,
              color: '#666',
              lineHeight: 1.6,
              margin: 0,
            }}>
              Looks like you haven't made <br />
              your order yet.
            </p>
          </section>
        </main>
      );
    }
    return null;
  };

  const renderShopNowButton = (): JSX.Element | null => {
    if (list.length > 0 || menuLoading) return null;
    return (
      <section style={{ padding: '24px 20px', marginBottom: 8 }}>
        <components.Button
          text="Browse Menu"
          onClick={() =>
            navigate(Routes.TabNavigator, { state: { menuName: menu[0].name } })
          }
        />
      </section>
    );
  };

  const renderLoading = (): JSX.Element | null => {
    if (menuLoading) return <components.Loader />;
    return null;
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header showGoBack={true} showBasket={false} title="Cart" />
    );
  };

  // Render a floating container for QR display.
  // When showQR is false, show the "Show QR to Captain" button in a fixed container.
  // When showQR is true, render a full-screen overlay with a large QR code and a close button.
  const renderFloatingButton = (): JSX.Element | null => {
    if (list.length === 0 && !menuLoading) return null;
    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50,
      }}>
        {/* If QR is not open, show the floating button */}
        {!showQR && (
          <div style={{
            backgroundColor: '#ffffff',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '20px 20px 0 0',
            padding: '20px',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
          }}>
            <components.Button
              text="Show QR to Captain"
              onClick={() => setShowQR(true)}
            />
          </div>
        )}
        {/* Full-screen overlay for QR */}
        {showQR && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            padding: 20,
          }}>
            <button
              onClick={() => setShowQR(false)}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                border: 'none',
                fontSize: 28,
                color: '#333',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'DM Sans, sans-serif',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              &times;
            </button>
            <div style={{
              backgroundColor: '#ffffff',
              padding: 24,
              borderRadius: 20,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
            }}>
              <QRCodeSVG value={qrData} size={280} />
            </div>
            <p style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 16,
              color: '#666',
              marginTop: 20,
              textAlign: 'center',
              fontWeight: 500,
            }}>
              Show this QR code to the Captain
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="screen" style={{ opacity }}>
      {renderHeader()}
      {renderEmpty()}
      {renderContent()}
      {renderLoading()}
      {renderShopNowButton()}
      {renderFloatingButton()}
    </div>
  );
};
